import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { BRAIN_COLOR_PALETTE } from "@/lib/constants/colors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateBrain, useGetBrainPersonality } from "@/lib/query/hooks";
import { useToast } from "@/hooks/use-toast";
import { useColonyContext } from "@/hooks/useColonyContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const EditBrain = () => {
  const navigate = useNavigate();
  const { colonyId, brainId } = useParams<{ colonyId: string; brainId: string }>();
  const { activeColonyId } = useColonyContext();
  const { toast } = useToast();
  
  const [name, setName] = useState("");
  const [personality, setPersonality] = useState("");
  const [selectedModelId, setSelectedModelId] = useState<string>("gemini-flash-life");
  const [selectedModelLabel, setSelectedModelLabel] = useState<string>("Gemini 2.5 Flash Life (Free)");
  const [selectedColor, setSelectedColor] = useState<string>("#6366f1");
  const [isLoading, setIsLoading] = useState(true);

  // Get current user info for subscription tier
  const savedUserStr = typeof window !== 'undefined' ? localStorage.getItem('lotteryOfLife_user') : null;
  const currentUserId = (() => {
    try { return savedUserStr ? JSON.parse(savedUserStr).userId : null; } catch { return null; }
  })();
  const subscriptionTier = (() => {
    try { return savedUserStr ? (JSON.parse(savedUserStr).subscriptionTier || 'free') : 'free'; } catch { return 'free'; }
  })();

  const modelOptions: Array<{ id: string; label: string; tier: 'free' | 'pro'; }> = [
    { id: 'gemini-flash-life', label: 'Gemini 2.5 Flash Life (Free)', tier: 'free' },
    { id: 'gemini-flash',      label: 'Gemini 2.5 Flash (Pro)',       tier: 'pro'  },
    { id: 'gemini-2.5-pro',    label: 'Gemini 2.5 Pro (Pro)',         tier: 'pro'  },
    { id: 'openai-gpt-5',      label: 'OpenAI GPT‑5 (Pro)',           tier: 'pro'  },
  ];

  // Use colonyId from URL or active colony
  const effectiveColonyId = colonyId || activeColonyId;
  
  // Fetch existing brain data
  const { data: brainPersonality, isLoading: isFetching, error } = useGetBrainPersonality(
    currentUserId || '',
    effectiveColonyId || '',
    brainId || ''
  );

  // Update form hooks
  const updateBrainMutation = useUpdateBrain(effectiveColonyId || '');

  // Populate form when data loads
  useEffect(() => {
    if (brainPersonality && !isFetching) {
      setName(brainPersonality.name || '');
      setPersonality(brainPersonality.personality || '');
      setSelectedColor(brainPersonality.color || '#6366f1');
      
      // Set model preference
      const modelPref = brainPersonality.modelPreference || 'gemini-flash-life';
      setSelectedModelId(modelPref);
      const modelOption = modelOptions.find(opt => opt.id === modelPref);
      if (modelOption) {
        setSelectedModelLabel(modelOption.label);
      }
      
      setIsLoading(false);
    }
  }, [brainPersonality, isFetching]);

  // Handle error cases
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load brain data. Please try again.",
        variant: "destructive"
      });
      navigate(`/chat/${effectiveColonyId}`);
    }
  }, [error, effectiveColonyId, navigate, toast]);

  // Block editing Alt-me
  useEffect(() => {
    if (brainId === 'alt-me') {
      toast({
        title: "Cannot Edit Alt-me",
        description: "Alt-me brain cannot be modified.",
        variant: "destructive"
      });
      navigate(`/chat/${effectiveColonyId}`);
    }
  }, [brainId, effectiveColonyId, navigate, toast]);

  const handleSelectModel = (id: string) => {
    const option = modelOptions.find(o => o.id === id);
    if (!option) return;
    if (option.tier === 'pro' && subscriptionTier !== 'pro') {
      toast({
        title: 'Pro model',
        description: 'Upgrade to Pro to use this model.',
      });
      return;
    }
    setSelectedModelId(option.id);
    setSelectedModelLabel(option.label);
  };

  const handleUpdate = async () => {
    console.debug('[EditBrain] Updating brain with', {
      brainId,
      colonyId: effectiveColonyId,
      name,
      personalityPreview: personality.slice(0, 50),
      model: selectedModelId
    });

    if (!effectiveColonyId || !brainId || !currentUserId) {
      toast({
        title: "Error", 
        description: "Missing required information to update brain.",
        variant: "destructive"
      });
      return;
    }

    if (!name.trim() || !personality.trim()) {
      toast({
        title: "Error", 
        description: "Please fill in both name and personality fields.",
        variant: "destructive"
      });
      return;
    }

    // 🚨 TOKEN COST CONTROL: Limit personality length
    const MAX_PERSONALITY_LENGTH = 500; // ~125 tokens
    if (personality.trim().length > MAX_PERSONALITY_LENGTH) {
      toast({
        title: "Personality Too Long", 
        description: `Please keep personality under ${MAX_PERSONALITY_LENGTH} characters to control token costs. Current: ${personality.length}`,
        variant: "destructive"
      });
      return;
    }

    try {
      await updateBrainMutation.mutateAsync({
        userId: currentUserId,
        colonyId: effectiveColonyId,
        brainId: brainId,
        persona: {
          id: brainId,
          name: name.trim(),
          personality: personality.trim(), // 🎭 THE UPDATED ROLEPLAY DESCRIPTION!
          modelPreference: selectedModelId,
          color: selectedColor,
          tone: "professional", // Keep existing or default tone
          humor: "light",       // Keep existing or default humor
          specialties: []       // Could be extracted from personality later
        }
      });

      toast({
        title: "Success",
        description: `${name} has been updated successfully!`
      });

      navigate(`/chat/${effectiveColonyId}/${brainId}`);
    } catch (error) {
      console.error("Failed to update brain:", error);
      toast({
        title: "Error",
        description: "Failed to update brain. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Show loading state
  if (isLoading || isFetching) {
    return (
      <div className="min-h-screen bg-background safe-area-top safe-area-bottom">
        <div className="flex items-center justify-between px-4 py-3 border-b border-ios-separator bg-ios-surface">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/chat/${effectiveColonyId}`)}
            className="text-foreground hover:bg-ios-surface-secondary w-11 h-11 rounded-full"
          >
            <ChevronLeft size={22} />
          </Button>
          <h1 className="ios-title text-foreground">Edit Brain</h1>
          <div className="w-11" />
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="text-muted-foreground">Loading brain data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ios-separator bg-ios-surface">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(`/chat/${effectiveColonyId}`)}
          className="text-foreground hover:bg-ios-surface-secondary w-11 h-11 rounded-full"
        >
          <ChevronLeft size={22} />
        </Button>
        <h1 className="ios-title text-foreground">Edit Brain</h1>
        <div className="w-11" />
      </div>

      {/* Form Content */}
      <div className="flex-1 px-4 py-6 space-y-6 pb-24">
        {/* Name Input */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Name</label>
          <Input 
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name"
            className="h-12 bg-ios-surface-secondary border-ios-separator text-foreground placeholder:text-muted-foreground rounded-xl"
          />
        </div>

        {/* Color Picker */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Color</label>
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-medium"
              style={{ backgroundColor: selectedColor }}
            >
              {name ? name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="grid grid-cols-8 gap-2">
              {BRAIN_COLOR_PALETTE.map(c => (
                <button
                  key={c.value}
                  onClick={() => setSelectedColor(c.value)}
                  className={`w-7 h-7 rounded-lg ${selectedColor === c.value ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
                  style={{ backgroundColor: c.value }}
                  title={c.name}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Personality Input */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-muted-foreground">Personality & Backstory</label>
            <span className={`text-xs ${personality.length > 400 ? 'text-red-500' : personality.length > 300 ? 'text-yellow-500' : 'text-muted-foreground'}`}>
              {personality.length}/500
            </span>
          </div>
          <Textarea 
            value={personality}
            onChange={(e) => setPersonality(e.target.value)}
            placeholder="Describe the brain's traits (e.g., 'You are Dr. Sarah Chen, a behavioral economist...')"
            className="min-h-[120px] bg-ios-surface-secondary border-ios-separator text-foreground placeholder:text-muted-foreground resize-none rounded-xl"
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">
            💡 Tip: Be specific but concise. This text is sent with every message to maintain personality.
          </p>
        </div>

        {/* Model Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Model</label>
          <div className="relative">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  className="w-full h-12 bg-ios-surface-secondary border-ios-separator text-left justify-between hover:bg-ios-surface-tertiary rounded-xl"
                >
                  <span className="text-foreground">{selectedModelLabel}</span>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                {modelOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.id}
                    disabled={opt.tier === 'pro' && subscriptionTier !== 'pro'}
                    onClick={() => handleSelectModel(opt.id)}
                    className="flex items-center justify-between"
                  >
                    <span className={opt.tier === 'pro' && subscriptionTier !== 'pro' ? 'text-muted-foreground' : ''}>{opt.label}</span>
                    {selectedModelId === opt.id && <Check size={16} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Update Button */}
        <div className="pt-8">
          <Button 
            className="w-full h-12 bg-ios-blue text-white hover:bg-ios-blue/90 font-medium rounded-xl disabled:opacity-50"
            onClick={handleUpdate}
            disabled={!name.trim() || !personality.trim() || updateBrainMutation.isPending}
          >
            {updateBrainMutation.isPending ? 'Updating...' : 'Update Brain'}
          </Button>
        </div>
      </div>

      {/* Bottom tabs are provided globally; no local bottom nav here */}
    </div>
  );
};

export default EditBrain;